import * as React from "react";
import { useState } from "react";
import { ImageOff, Video as VideoIcon, VideoOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { EmptyState, ErrorState, LoadingState } from "../../components/patterns/states";
import type { Attachment } from "../../backend/api/chat";
import {
  attachmentThumbnailUrl,
  attachmentUrl,
  participantName,
} from "../../backend/api/chat";
import { useConversation, useConversationMedia } from "../../backend/queries/chat";
import { relativeMessageTime } from "./chatFormat";
import { MediaLightbox } from "./MediaLightbox";
import { TOUCH_CLOSE } from "./dialogClose";
import { useFocusReturn } from "./useFocusReturn";

export type MediaSidebarProps = {
  conversationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type KindFilter = "all" | Attachment["kind"];

const FILTERS: { value: KindFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "image", label: "Images" },
  { value: "video", label: "Videos" },
  { value: "document", label: "Documents" },
];

/**
 * Everything shared in one conversation, filterable by kind.
 *
 * The participant list comes from `useConversation` rather than a prop,
 * because the only prop this component takes is the conversation id - the
 * caller already fetches it for the thread, but re-fetching here keeps this
 * component self-contained and cheap to mount from anywhere.
 */
export function MediaSidebar({
  conversationId,
  open,
  onOpenChange,
}: MediaSidebarProps) {
  const focusReturn = useFocusReturn(open);
  const [filter, setFilter] = useState<KindFilter>("all");
  const [selected, setSelected] = useState<Attachment | null>(null);
  // Fetched only once the sheet opens, since most people never open it. What
  // it loaded stays cached for the next time.
  const media = useConversationMedia(
    conversationId,
    filter === "all" ? undefined : filter,
    { enabled: open },
  );
  const conversation = useConversation(conversationId);

  const attachments = media.data ?? [];
  const sorted = [...attachments].sort((a, b) =>
    a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
  );

  const sharedByName = (userId: string): string => {
    const participant = conversation.data?.participants.find(
      (candidate) => candidate.user_id === userId,
    );
    return participant ? participantName(participant) : "Portal user";
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col sm:max-w-md"
          closeClassName={TOUCH_CLOSE}
          // The sheet has a title and no description. Without this Radix points
          // aria-describedby at a description that does not exist.
          aria-describedby={undefined}
          {...focusReturn}
        >
          <SheetHeader>
            <SheetTitle>Shared media</SheetTitle>
          </SheetHeader>

          <Tabs
            value={filter}
            onValueChange={(value) => setFilter(value as KindFilter)}
            className="flex min-h-0 flex-1 flex-col gap-4"
          >
            {/* Wraps instead of running off the sheet when text is enlarged. */}
            <TabsList className="mx-4 h-auto flex-wrap justify-start">
              {FILTERS.map((option) => (
                <TabsTrigger
                  key={option.value}
                  value={option.value}
                  className="min-h-touch min-w-touch md:min-h-9 md:min-w-0"
                >
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* The tabs name this panel through aria-controls, so it must be the
                tab panel, not a sibling. */}
            <TabsContent
              value={filter}
              className="mt-0 flex-1 overflow-y-auto px-4 pb-4"
            >
            {media.isLoading ? (
              <LoadingState label="Loading shared media" />
            ) : media.isError ? (
              <ErrorState
                title="Shared media could not be loaded."
                onRetry={() => media.refetch()}
              />
            ) : sorted.length === 0 ? (
              <EmptyState
                title="Nothing shared yet"
                description="Photos, videos, and documents anyone sends in this conversation will show up here, so the move file stays in one place."
              />
            ) : (
              <>
                <ul className="flex flex-col gap-2">
                  {sorted.map((attachment) => (
                    <li key={attachment.id}>
                      <MediaRow
                        attachment={attachment}
                        sharedBy={sharedByName(attachment.uploaded_by)}
                        onOpen={() => setSelected(attachment)}
                      />
                    </li>
                  ))}
                </ul>
                {media.hasNextPage ? (
                  <div className="flex justify-center pt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="min-h-touch md:min-h-0"
                      onClick={() => void media.fetchNextPage()}
                      disabled={media.isFetchingNextPage}
                    >
                      {media.isFetchingNextPage
                        ? "Loading more"
                        : media.isFetchNextPageError
                          ? "Could not load more. Try again"
                          : "Show more"}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      <MediaLightbox attachment={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function MediaRow({
  attachment,
  sharedBy,
  onOpen,
}: {
  attachment: Attachment;
  sharedBy: string;
  onOpen: () => void;
}) {
  const [previewFailed, setPreviewFailed] = useState(false);
  const when = relativeMessageTime(attachment.created_at);
  const name = attachment.original_name || "Untitled file";

  if (attachment.kind === "document") {
    return (
      <a
        href={attachmentUrl(attachment.id)}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 rounded-lg border p-2 hover:bg-muted"
      >
        <span
          aria-hidden="true"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold uppercase text-muted-foreground"
        >
          {fileExtension(name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {formatFileSize(attachment.size_bytes)} &middot; Shared by{" "}
            {sharedBy} &middot; {when}
          </p>
        </div>
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-lg border p-2 text-left hover:bg-muted"
    >
      <span className="relative flex aspect-square h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {previewFailed ? (
          // No preview for this source, such as a HEIC photo or a video sent
          // without a poster. The row still opens the original.
          attachment.kind === "video" ? (
            <VideoOff className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ImageOff className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          )
        ) : (
          <img
            src={attachmentThumbnailUrl(attachment.id)}
            alt={name}
            loading="lazy"
            crossOrigin="use-credentials"
            onError={() => setPreviewFailed(true)}
            className="h-full w-full object-cover"
          />
        )}
        {attachment.kind === "video" && !previewFailed && (
          <VideoIcon
            aria-hidden="true"
            className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow"
          />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="truncate text-xs text-muted-foreground">
          Shared by {sharedBy} &middot; {when}
        </p>
      </div>
    </button>
  );
}

function fileExtension(name: string): string {
  const parts = name.split(".");
  const ext = parts.length > 1 ? parts[parts.length - 1] : "";
  return ext ? ext.slice(0, 4).toUpperCase() : "FILE";
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "Unknown size";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(exponent ? 1 : 0)} ${units[exponent]}`;
}
